import React, { useState } from 'react';
import { CalendarClock, PackageCheck } from 'lucide-react';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import { useJobs } from '../hooks/useJobs';
import { formatShortDate } from '../utils/date-utils';

const INK    = '#2B2620';
const WALNUT = '#8B4513';
const MOSS   = '#3B6B44';
const OCHRE  = '#A6752A';
const STONE  = '#8C8275';
const PAPER  = '#FDFBF8';
const FONT_SANS = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const SIDEBAR_KEY = 'twp-sidebar-collapsed';

export default function AppLayout({ title, breadcrumb, topBarActions, fullBleed = false, children }) {
  // ── Sidebar open or small: stays the way you left it, on every page and after a refresh.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_KEY) === '1'; } catch { return false; }
  });
  const toggleSidebar = () => setSidebarCollapsed((c) => {
    try { localStorage.setItem(SIDEBAR_KEY, c ? '0' : '1'); } catch { /* private mode: just this page */ }
    return !c;
  });
  const { jobService } = useJobs();

  const furthestScheduled = jobService.getFurthestScheduledDate();
  const soldOutThrough    = jobService.getSoldOutThroughDate();

  const soldOutDisplay = soldOutThrough
    ? (typeof soldOutThrough === 'string' ? soldOutThrough : formatShortDate(soldOutThrough))
    : 'No backlog';

  return (
    <div style={{
      display: 'flex',
      height: '100vh',
      overflow: 'hidden',
      background: 'var(--bg-app-gradient)'
    }}>
      {/* Left Sidebar */}
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggle={toggleSidebar}
      />

      {/* Main Content Area */}
      <div style={{
        marginLeft: sidebarCollapsed ? '80px' : '260px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        transition: 'margin-left 0.3s ease'
      }}>
        {/* Top Bar */}
        <TopBar title={title} breadcrumb={breadcrumb}>
          {topBarActions}
        </TopBar>

        {/* Page Content — the only scrollable region, so the footer below
            always stays visible without scrolling the whole page. */}
        <main style={{
          flex: 1,
          // fullBleed: the page fills the whole area (the Website pages bring their own spacing).
          padding: fullBleed ? 0 : '32px',
          overflowY: fullBleed ? 'hidden' : 'auto',
          minHeight: 0
        }}>
          {children}
        </main>

        {/* Footer — always visible, pinned below the scrollable main area */}
        <footer style={{
          height: '60px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 32px',
          background: PAPER,
          borderTop: '1px solid rgba(139,69,19,0.1)',
          fontFamily: FONT_SANS,
        }}>
          {/* Left: Schedule Info */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '7px 14px',
              background: `${WALNUT}0f`,
              border: `1px solid ${WALNUT}26`,
              borderRadius: '8px',
            }}>
              <CalendarClock size={14} strokeWidth={2} color={OCHRE} />
              <span style={{ fontSize: '11.5px', color: STONE, fontWeight: '500' }}>
                Scheduled Through
              </span>
              <span style={{ fontSize: '12.5px', color: INK, fontWeight: '700' }}>
                {furthestScheduled ? formatShortDate(furthestScheduled) : 'No jobs scheduled'}
              </span>
            </div>

            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '7px 14px',
              background: `${WALNUT}0f`,
              border: `1px solid ${WALNUT}26`,
              borderRadius: '8px',
            }}>
              <PackageCheck size={14} strokeWidth={2} color={MOSS} />
              <span style={{ fontSize: '11.5px', color: STONE, fontWeight: '500' }}>
                Sold Out Through
              </span>
              <span style={{ fontSize: '12.5px', color: INK, fontWeight: '700' }}>
                {soldOutDisplay}
              </span>
            </div>
          </div>

          {/* Right: Credits */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
            <div style={{ fontSize: '10.5px', color: STONE, fontWeight: '500' }}>
              © {new Date().getFullYear()} The Window Place LLC
            </div>
            <div style={{ fontSize: '10.5px', color: STONE, fontWeight: '500' }}>
              App by Kwizera Digital Services
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}